import { createFileRoute, Link } from "@tanstack/react-router";
import { StaticPage, staticHead } from "@/components/StaticPage";
import { ADS_ENABLED } from "@/lib/ads";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
  head: () =>
    staticHead({
      title: "Privacy Policy | AutoSpecData",
      description:
        "How AutoSpecData handles visitor data: what is collected, cookies and advertising, third-party services, your choices, and how to contact us about your data.",
      path: "/privacy",
    }),
});

function PrivacyPage() {
  return (
    <StaticPage
      title="Privacy Policy"
      intro="This policy explains what information AutoSpecData collects when you visit, why, and what control you have over it."
    >
      <section>
        <h2>What we collect</h2>
        <p>
          You can browse every vehicle page without creating an account. We do not ask for
          personal information to read the site. If you send us a message through the
          contact form, we store the name, email address, subject, and message you submit
          so we can reply. We do not sell that information or use it for marketing.
        </p>
      </section>
      <section>
        <h2>Server logs</h2>
        <p>
          Like any website, our hosting provider records standard request data — IP address,
          browser user agent, requested URL, and timestamp — to serve pages, prevent abuse,
          and diagnose faults. These logs are retained only as long as needed for those
          purposes.
        </p>
      </section>
      <section>
        <h2>Cookies and advertising</h2>
        {ADS_ENABLED ? (
          <>
            <p>
              This site displays advertising served by Google AdSense. Google and its
              partners may use cookies or device identifiers to serve ads based on your prior
              visits to this and other websites.
            </p>
            <ul>
              <li>
                Google's use of advertising cookies enables it and its partners to serve ads
                based on your visits to this site and other sites on the internet.
              </li>
              <li>
                You can opt out of personalized advertising in{" "}
                <a
                  href="https://www.google.com/settings/ads"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google Ads Settings
                </a>
                .
              </li>
              <li>
                You can opt out of third-party vendor cookies at{" "}
                <a
                  href="https://www.aboutads.info/choices/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  aboutads.info/choices
                </a>
                .
              </li>
              <li>
                Google's advertising practices are described in the{" "}
                <a
                  href="https://policies.google.com/technologies/partner-sites"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google Privacy &amp; Terms
                </a>
                .
              </li>
            </ul>
          </>
        ) : (
          <p>
            AutoSpecData does not currently serve advertising and sets no advertising cookies.
            If that changes, this section will describe the ad partner, the cookies it uses,
            and how to opt out — before any ad is shown.
          </p>
        )}
      </section>
      <section>
        <h2>Analytics</h2>
        <p>
          We may use privacy-respecting analytics to count page views and understand which
          pages are useful. Analytics data is aggregated and is not used to identify
          individual visitors.
        </p>
      </section>
      <section>
        <h2>Your choices</h2>
        <ul>
          <li>Block or delete cookies in your browser settings at any time.</li>
          <li>Use browser tracking protection or an ad blocker — the site still works.</li>
          <li>
            Ask us to delete a message you sent through the contact form by writing to us
            from the same email address.
          </li>
        </ul>
      </section>
      <section>
        <h2>Children</h2>
        <p>
          AutoSpecData is a general-audience reference site and is not directed at children
          under 13. We do not knowingly collect information from them.
        </p>
      </section>
      <section>
        <h2>Changes and contact</h2>
        <p>
          If this policy changes materially, the updated version will be posted on this
          page. Questions about privacy go through the{" "}
          <Link to="/contact">contact page</Link>.
        </p>
      </section>
    </StaticPage>
  );
}
